// src/pages/DealerDetail.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Breadcrumbs from "../components/Breadcrumbs";
import HistoricalReportModal from "../components/HistoricalReportModal";

// Helpers for formatting
const formatCurrency = (val: number | string | null) => {
  if (val == null) return "N/A";
  const num = Number(val);
  if (isNaN(num)) return "N/A";
  // preserves decimals if present, comma‑separates thousands, prefixes $
  return `$${num.toLocaleString("en-US")}`;
};

const formatPercentage = (val: number | string | null) => {
  if (val == null) return "N/A";
  const num = Number(val);
  if (isNaN(num)) return "N/A";
  // comma‑separate and append %
  return `${num.toLocaleString("en-US")}%`;
};

  const tdStyle: React.CSSProperties = {
  padding: "8px",
  borderBottom: "1px solid #ccc"
};


interface Dealer {
  id: number;
  company_name: string;
  created_at: string;
  country_id: number;
  country_name: string;
}

// Updated Report interface to include both date and created_at as optional.
// For historical dummy reports, "date" will be provided.
// For new reports from the DB, "created_at" will be used.
interface Report {
  id: number;
  date?: string;
  created_at?: string;
  title?: string;
  summary?: string;
  content?: string;
}

const DealerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dealer, setDealer] = useState<Dealer | null>(null);

  // State for active tabs for the newest report (split into 3 card groups)
  const [activeCard1Tab, setActiveCard1Tab] = useState("trainingAudit");
  const [activeCard2Tab, setActiveCard2Tab] = useState("customerSatisfaction");
  const [activeCard3Tab, setActiveCard3Tab] = useState("dealerSupportNeeds");

  // State for the historical report selected to display in the popup
  const [selectedHistoricalReport, setSelectedHistoricalReport] = useState<Report | null>(null);

  // State for reports array and the selected (newest) report id
  const [reports, setReports] = useState<Report[]>([]);
  const [newestReportId, setNewestReportId] = useState<number | null>(null);

  // States for each section's data fetched from backend (via report-centric endpoints)
  const [salesTrainingData, setSalesTrainingData] = useState<any>(null);
  const [technicalSupportData, setTechnicalSupportData] = useState<any>(null);
  const [inventoryAuditData, setInventoryAuditData] = useState<any>(null);
  const [marketConditionsData, setMarketConditionsData] = useState<any>(null);
  const [customerSatisfactionData, setCustomerSatisfactionData] = useState<any>(null);
  const [installationAuditData, setInstallationAuditData] = useState<any>(null);
  const [financialDataData, setFinancialDataData] = useState<any>(null);
  const [dealerSupportNeedsData, setDealerSupportNeedsData] = useState<any>(null);
  const [dealerMarketingSupportData, setDealerMarketingSupportData] = useState<any>(null);
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [finalSummaryData, setFinalSummaryData] = useState<any>(null);



  const formatDate = (dateStr: string) => {
    const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateStr).toLocaleDateString(undefined, options);
  };
  
  

  // Fetch dealer details
  useEffect(() => {
    fetch(`/api/dealers/${id}`)
      .then((res) => res.json())
      .then((data) => setDealer(data))
      .catch((err) => console.error("Error fetching dealer:", err));
  }, [id]);

  // Fetch reports for the dealer
  useEffect(() => {
    fetch(`/api/dealers/${id}/reports`)
      .then((res) => res.json())
      .then((data: Report[]) => {
        setReports(data);
        if (data && data.length > 0) {
          // Choose the newest report (assumed sorted by created_at DESC)
          setNewestReportId(data[0].id);
        }
      })
      .catch((err) => console.error("Error fetching reports:", err));
  }, [id]);

  // When newestReportId is set, fetch section data using report-centric endpoints
  useEffect(() => {
    if (!newestReportId) return;

    fetch(`/api/reports/${newestReportId}/sales-training`)
      .then((res) => res.json())
      .then((data) => setSalesTrainingData(data))
      .catch(() => setSalesTrainingData(null));

    fetch(`/api/reports/${newestReportId}/technical-support`)
      .then((res) => res.json())
      .then((data) =>
        setTechnicalSupportData(Array.isArray(data) && data.length > 0 ? data[0] : null)
      )
      .catch(() => setTechnicalSupportData(null));

    fetch(`/api/reports/${newestReportId}/inventory-audit`)
      .then((res) => res.json())
      .then((data) => setInventoryAuditData(data))
      .catch(() => setInventoryAuditData(null));

    fetch(`/api/reports/${newestReportId}/market-conditions`)
      .then((res) => res.json())
      .then((data) =>
        setMarketConditionsData(Array.isArray(data) && data.length > 0 ? data[0] : null)
      )
      .catch(() => setMarketConditionsData(null));

    fetch(`/api/reports/${newestReportId}/customer-satisfaction`)
      .then((res) => res.json())
      .then((data) => setCustomerSatisfactionData(data))
      .catch(() => setCustomerSatisfactionData(null));

    fetch(`/api/reports/${newestReportId}/installation-audit`)
      .then((res) => res.json())
      .then((data) => setInstallationAuditData(data))
      .catch(() => setInstallationAuditData(null));

    fetch(`/api/reports/${newestReportId}/financial-data`)
      .then((res) => res.json())
      .then((data) => setFinancialDataData(data))
      .catch(() => setFinancialDataData(null));

    fetch(`/api/reports/${newestReportId}/dealer-support-needs`)
      .then((res) => res.json())
      .then((data) => setDealerSupportNeedsData(data && Object.keys(data).length ? data : null))
      .catch(() => setDealerSupportNeedsData(null));

    fetch(`/api/reports/${newestReportId}/dealer-marketing-support`)
      .then((res) => res.json())
      .then((data) => setDealerMarketingSupportData(data))
      .catch(() => setDealerMarketingSupportData(null));

    fetch(`/api/reports/${newestReportId}/feedback`)
      .then((res) => res.json())
      .then((data) => setFeedbackData(data))
      .catch(() => setFeedbackData(null));

    fetch(`/api/reports/${newestReportId}/final-summary`)
      .then((res) => res.json())
      .then((data) => setFinalSummaryData(data))
      .catch(() => setFinalSummaryData(null));
  }, [newestReportId]);
  
  const handleCreateReport = async () => {
  try {
    const res = await fetch(`/api/dealers/${id}/reports`, { method: "POST" });
    if (!res.ok) throw new Error("Failed to create report");
    const newReport: Report = await res.json();
    // Insert at front of reports array
    setReports(prev => [newReport, ...prev]);
    // Make it the active “newest” report
    setNewestReportId(newReport.id);
  } catch (err) {
    console.error(err);
    alert("Could not create report. Please try again.");
  }
};

const handleDeleteReport = async () => {
  if (!newestReportId) return;
  // 1) prompt for exact word "DELETE"
  const confirmation = window.prompt(
    'Type DELETE to confirm deletion of the newest report.'
  );
  if (confirmation !== "DELETE") {
    alert("Deletion cancelled.");
    return;
  }

  // 2) call backend
  try {
    const res = await fetch(
      `/api/dealers/${id}/reports/${newestReportId}`,
      { method: "DELETE" }
    );
    if (!res.ok) throw new Error("Failed to delete");
    // 3) remove from state
    setReports(prev => prev.filter(r => r.id !== newestReportId));
    // 4) pick next newest (first in the remaining array)
    setNewestReportId(prevList => {
      const remaining = reports.filter(r => r.id !== newestReportId);
      return remaining.length > 0 ? remaining[0].id : null;
    });
  } catch (err) {
    console.error(err);
    alert("Could not delete report. Please try again.");
  }
};



  // Section header style to mimic Risk Assessment styling
  const sectionHeaderStyle: React.CSSProperties = {
    color: "#e41e26",
    borderBottom: "1px solid #e41e26",
    paddingBottom: "5px",
    marginTop: "15px",
    fontSize: "16px",
  };
  



  // Dummy report object is used for tab labels and structure.
  // The actual content is populated via section states.
  const dummyReport = {
    trainingAudit: {
      title: "Sales Training",
      content: (
        <>
          <p>
            <strong>Objective:</strong> Ensure all dealer sales reps are fully trained in wind machine mapping and surveys.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Sales Rep Name</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Training Level</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {salesTrainingData && Array.isArray(salesTrainingData) && salesTrainingData.length > 0 ? (
                salesTrainingData.map((rep: any, index: number) => (
                  <tr key={index}>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{rep.name || "N/A"}</td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{rep.training_level || "N/A"}</td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{rep.notes || "N/A"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      ),
    },
    technicalSupport: {
      title: "Technical Support",
      content: (
        <div>
		<p>
            <strong>Objective:</strong> Address dealer technical questions.
          </p>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Support Summary</h3>
            <p>{technicalSupportData?.questions_asked || "N/A"}</p>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Resolved</h3>
            <p>
              {technicalSupportData?.resolved !== undefined
                ? technicalSupportData.resolved
                  ? "Yes"
                  : "No"
                : "N/A"}
            </p>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Follow-Up Actions</h3>
            <p>
              {technicalSupportData?.meeting_scheduled !== undefined
                ? technicalSupportData.meeting_scheduled
                  ? "Yes"
                  : "No"
                : "N/A"}
            </p>
            <h3 style={sectionHeaderStyle}>Date of Scheduled Meeting</h3>
			<p>
			{technicalSupportData?.meeting_date
			  ? formatDate(technicalSupportData.meeting_date)
			  : "N/A"}
		  </p>
          </div>
        </div>
      ),
    },
    inventoryAudit: {
      title: "Inventory Audit",
      content: (
        <>
          <p>
            <strong>Objective:</strong> Verify Orchard-Rite inventory levels and check for aftermarket vs. genuine parts.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Item</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Quantity</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Genuine (Y/N)</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {inventoryAuditData && Array.isArray(inventoryAuditData) && inventoryAuditData.length > 0 ? (
                inventoryAuditData.map((item: any, index: number) => (
                  <tr key={index}>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{item.item || "N/A"}</td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{item.quantity || "N/A"}</td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
                      {item.genuine !== undefined ? (item.genuine ? "Yes" : "No") : "N/A"}
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{item.notes || "N/A"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      ),
    },
    marketConditions: {
  title: "Market Conditions",
  content: (
    <div>
	<p>
            <strong>Objective:</strong> Investigate current market conditions for the dealer and their region.
     </p>
      <div style={{ marginBottom: "10px" }}>
        <h3 style={sectionHeaderStyle}>Crop Loss & Insurance</h3>
        <p>Average Crop Loss: {marketConditionsData?.crop_loss ?? "N/A"}%</p>
        <p>
          Crop Insurance:{" "}
          {marketConditionsData?.crop_insurance === true
            ? "Yes"
            : marketConditionsData?.crop_insurance === false
            ? "No"
            : "N/A"}
        </p>
      </div>

      <div style={{ marginBottom: "10px" }}>
        <h3 style={sectionHeaderStyle}>Returns & Subsidies</h3>
               <p>
         Returns on Crops:{" "}
         {marketConditionsData?.returns != null
           ? `${marketConditionsData.returns}%`
           : "N/A"}
       </p>
        <p>
          Government Subsidies:{" "}
          {marketConditionsData?.government_subsidies || "N/A"}
        </p>
      </div>

      <div style={{ marginBottom: "10px" }}>
  <h3 style={sectionHeaderStyle}>Market Share</h3>
  <p>
    {marketConditionsData?.our_company_name || "Our Company"}:{" "}
    {marketConditionsData?.our_company_share != null
      ? `${marketConditionsData.our_company_share}%`
      : "N/A"}
  </p>
  {[1, 2, 3].map((i) => {
    const name = marketConditionsData?.[`competitor${i}_name` as any];
    const share = marketConditionsData?.[`competitor${i}_share` as any];

    // only render if there's a name
    if (!name) return null;

    return (
      <p key={i}>
        {name}:{" "}
        {share != null
          ? `${share}%`
          : "N/A"}
      </p>
    );
  })}
</div>


      <div style={{ marginBottom: "10px" }}>
        <h3 style={sectionHeaderStyle}>Customer Preferences</h3>
        <p>
          Why Customers Choose Us:{" "}
          {marketConditionsData?.why_choose_us || "N/A"}
        </p>
        <p>
          Why Customers Choose Competitors:{" "}
          {marketConditionsData?.why_choose_competitors || "N/A"}
        </p>
      </div>
    </div>
  ),
},

    customerSatisfaction: {
      title: "Customer Satisfaction",
      content: (
        <>
          <p>
            <strong>Objective:</strong> Evaluate product satisfaction and dealer service quality.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Customer Name</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Company</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Product Rating</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Service Rating</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Comments</th>
              </tr>
            </thead>
            <tbody>
  {customerSatisfactionData && customerSatisfactionData.length > 0 ? (
    customerSatisfactionData.map((entry: any, index: number) => (
      <tr key={index}>
        <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
          {entry.customer_name || "N/A"}
        </td>
        <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
          {entry.company || "N/A"}
        </td>

        {/* Product Rating → 5 stars */}
        <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
          {typeof entry.product_rating === "number" ? (
            Array.from({ length: 5 }).map((_, i) => (
              <span key={i} style={{ color: "#e41e26", fontSize: "1.1em" }}>
                {i < entry.product_rating ? "★" : "☆"}
              </span>
            ))
          ) : (
            "N/A"
          )}
        </td>

        {/* Service Rating → 5 stars */}
        <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
          {typeof entry.dealer_service_rating === "number" ? (
            Array.from({ length: 5 }).map((_, i) => (
              <span key={i} style={{ color: "#e41e26", fontSize: "1.1em" }}>
                {i < entry.dealer_service_rating ? "★" : "☆"}
              </span>
            ))
          ) : (
            "N/A"
          )}
        </td>

        <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
          {entry.comments || "N/A"}
        </td>
      </tr>
    ))
  ) : (
    <tr>
      <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
      <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
      <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
      <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
      <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
    </tr>
  )}
</tbody>

          </table>
        </>
      ),
    },
    installationAudit: {
      title: "Installation Audit",
      content: (
        <>
          <p>
            <strong>Objective:</strong> Verify that grower installations follow Orchard-Rite guidelines and protocols.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Grower Name</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Installation Location</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Guidelines Followed?</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Issues Found?</th>
              </tr>
            </thead>
            <tbody>
              {installationAuditData && Array.isArray(installationAuditData) && installationAuditData.length > 0 ? (
                installationAuditData.map((entry: any, index: number) => (
                  <tr key={index}>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{entry.grower_name || "N/A"}</td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{entry.installation_location || "N/A"}</td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
                      {entry.guidelines_followed !== undefined ? (entry.guidelines_followed ? "Yes" : "No") : "N/A"}
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{entry.issues_found || "N/A"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
                </tr>
              )}
            </tbody>
          </table>

        </>
      ),
    },
    financialData: {
      title: "Financial Data",
      content: (
        <>
          <p>
            <strong>Objective:</strong> Collect financial data for Orchard-Rite products to assess dealer performance.
          </p>
          {financialDataData &&
          Array.isArray(financialDataData) &&
          financialDataData.length > 0 ? (
            (() => {
              const record = financialDataData[0];
              return (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>
                        Financial Metric
                      </th>
                      <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>
                        Dealer Data
                      </th>
                    </tr>
                  </thead>
                  <tbody>
      <tr>
        <td style={tdStyle}>Annual Revenue</td>
        <td style={tdStyle}>{formatCurrency(record.annual_revenue)}</td>
      </tr>
      <tr>
        <td style={tdStyle}>New Units Sold (Last 12 Months)</td>
        <td style={tdStyle}>{record.new_units_sold ?? "N/A"}</td>
      </tr>
      <tr>
        <td style={tdStyle}>Average Selling Price per Unit</td>
        <td style={tdStyle}>{formatCurrency(record.avg_selling_price)}</td>
      </tr>
      <tr>
        <td style={tdStyle}>Profit Margin</td>
        <td style={tdStyle}>{formatPercentage(record.profit_margin)}</td>
      </tr>
      <tr>
        <td style={tdStyle}>Percentage of Total Business</td>
        <td style={tdStyle}>{formatPercentage(record.percentage_of_total)}</td>
      </tr>
      <tr>
        <td style={tdStyle}>Number of Repeat Customers</td>
        <td style={tdStyle}>{record.num_repeat_customers ?? "N/A"}</td>
      </tr>
      <tr>
        <td style={tdStyle}>Total Parts Revenue</td>
        <td style={tdStyle}>{formatCurrency(record.total_parts_revenue)}</td>
      </tr>
      <tr>
        <td style={tdStyle}>Most Sold Model(s)</td>
        <td style={tdStyle}>{record.most_sold_models || "N/A"}</td>
      </tr>
      <tr>
        <td style={tdStyle}>Average Time to Close a Sale</td>
        <td style={tdStyle}>{record.avg_time_to_close ?? "N/A"}</td>
      </tr>
      <tr>
        <td style={tdStyle}>Top Customer Group</td>
        <td style={tdStyle}>{record.top_customer_group || "N/A"}</td>
      </tr>
    </tbody>
                </table>
              );
            })()
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Financial Metric</th>
                  <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Dealer Data</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>Annual Revenue</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>N/A</td>
                </tr>
              </tbody>
            </table>
          )}
        </>
      ),
    },
    dealerSupportNeeds: {
      title: "Support Needs",
      content: (
        <div>
		<p>
            <strong>Objective:</strong> Identify pain points and where the dealer needs additional support.
          </p>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Challenges</h3>
            <p>{dealerSupportNeedsData?.challenges || "N/A"}</p>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Action Plan</h3>
            <p>{dealerSupportNeedsData?.action_plan || "N/A"}</p>
          </div>
        </div>
      ),
    },
    dealerMarketingSupport: {
      title: "Marketing",
      content: (
        <div>
		<p>
            <strong>Objective:</strong> Ensure the dealer has adequate marketing materials and is effectively reaching potential customers.
          </p>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Marketing Materials</h3>
            <p>{dealerMarketingSupportData?.marketing_materials || "N/A"}</p>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Outreach Channels</h3>
            <p>{dealerMarketingSupportData?.outreach_channels || "N/A"}</p>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Additional Support</h3>
            <p>{dealerMarketingSupportData?.additional_support || "N/A"}</p>
          </div>
        </div>
      ),
    },
    feedback: {
      title: "Feedback & Insights",
      content: (
        <div>
		<p>
            <strong>Objective:</strong> Gather direct feedback from dealers and customers to improve product quality and value.
          </p>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Customer Complaints</h3>
            <p>{feedbackData?.customer_complaints || "N/A"}</p>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Desired Features</h3>
            <p>{feedbackData?.desired_features || "N/A"}</p>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Service Issues</h3>
            <p>{feedbackData?.service_issues || "N/A"}</p>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Escalation</h3>
            <p>{feedbackData?.escalation_details || "N/A"}</p>
          </div>
        </div>
      ),
    },
    finalSummary: {
      title: "Summary",
      content: (
        <div>
		<p>
            <strong>Objective:</strong> Final notes and summary of the dealer.
          </p>
           <div style={{ marginBottom: "10px" }}>
   <h3 style={sectionHeaderStyle}>Overall Rating</h3>
   <p>
     {typeof finalSummaryData?.overall_rating === "number" ? (
       Array.from({ length: 5 }).map((_, i) => (
         <span key={i} style={{ color: "#e41e26", fontSize: "1.1em" }}>
           {i < finalSummaryData.overall_rating ? "★" : "☆"}
         </span>
       ))
     ) : (
       "N/A"
     )}
   </p>
 </div>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Key Takeaways & Next Steps</h3>
            <p>{finalSummaryData?.key_takeaways || "N/A"}</p>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Representative Info</h3>
            <p>{finalSummaryData?.representative_name || "N/A"}</p>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <h3 style={sectionHeaderStyle}>Date</h3>
            <p>
              {finalSummaryData?.summary_date
                ? formatDate(finalSummaryData.summary_date)
                : "N/A"}
            </p>
          </div>
        </div>
      ),
    },
  };

  // Group the newest report sections into three card groups
  const card1Tabs = [
    { key: "trainingAudit", label: dummyReport.trainingAudit.title, content: dummyReport.trainingAudit.content },
    { key: "technicalSupport", label: dummyReport.technicalSupport.title, content: dummyReport.technicalSupport.content },
    { key: "inventoryAudit", label: dummyReport.inventoryAudit.title, content: dummyReport.inventoryAudit.content },
    { key: "marketConditions", label: dummyReport.marketConditions.title, content: dummyReport.marketConditions.content },
  ];

  const card2Tabs = [
    { key: "customerSatisfaction", label: dummyReport.customerSatisfaction.title, content: dummyReport.customerSatisfaction.content },
    { key: "installationAudit", label: dummyReport.installationAudit.title, content: dummyReport.installationAudit.content },
    { key: "financialData", label: dummyReport.financialData.title, content: dummyReport.financialData.content },
  ];

  const card3Tabs = [
    { key: "dealerSupportNeeds", label: dummyReport.dealerSupportNeeds.title, content: dummyReport.dealerSupportNeeds.content },
    { key: "dealerMarketingSupport", label: dummyReport.dealerMarketingSupport.title, content: dummyReport.dealerMarketingSupport.content },
    { key: "feedback", label: dummyReport.feedback.title, content: dummyReport.feedback.content },
    { key: "finalSummary", label: dummyReport.finalSummary.title, content: dummyReport.finalSummary.content },
  ];



  // Define edit route mappings (remain dealer-based)
  const editRoutesCard1: { [key: string]: string } = {
    trainingAudit: `/dealer/${id}/edit-sales-training`,
    technicalSupport: `/dealer/${id}/edit-technical-support`,
    inventoryAudit: `/dealer/${id}/edit-inventory-audit`,
    marketConditions: `/dealer/${id}/edit-market-conditions`,
  };
  const editRoutesCard2: { [key: string]: string } = {
    customerSatisfaction: `/dealer/${id}/edit-customer-satisfaction`,
    installationAudit: `/dealer/${id}/edit-installation-audit`,
    financialData: `/dealer/${id}/edit-financial-data`,
  };
  const editRoutesCard3: { [key: string]: string } = {
    dealerSupportNeeds: `/dealer/${id}/edit-dealer-support-needs`,
    dealerMarketingSupport: `/dealer/${id}/edit-dealer-marketing-support`,
    feedback: `/dealer/${id}/edit-feedback`,
    finalSummary: `/dealer/${id}/edit-final-summary`,
  };

  // Styling objects
  const containerStyle: React.CSSProperties = { backgroundColor: "#F0F5F0", minHeight: "100vh", padding: "20px" };
  const mainCardStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    maxWidth: "800px",
    margin: "50px auto",
    padding: "30px",
  };
  const headerStyle: React.CSSProperties = {
    textAlign: "center",
    color: "#e41e26",
    borderBottom: "2px solid #e41e26",
    paddingBottom: "10px",
    marginBottom: "20px",
    fontSize: "24px",
  };
  const subheaderStyle: React.CSSProperties = {
    textAlign: "center",
    color: "#e41e26",
    marginBottom: "20px",
    fontSize: "20px",
  };
  const tabContainerStyle: React.CSSProperties = {
    display: "flex",
    borderBottom: "1px solid #ccc",
    marginBottom: "20px",
  };
  const tabStyle: React.CSSProperties = {
    flex: 1,
    padding: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    backgroundColor: "#f0f0f0",
  };
  const activeTabStyle: React.CSSProperties = {
    ...tabStyle,
    backgroundColor: "#e41e26",
    color: "#fff",
    fontWeight: "bold",
  };
  const listItemStyle: React.CSSProperties = {
    padding: "10px",
    borderBottom: "1px solid #ccc",
    cursor: "pointer",
  };
  const popupOverlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  };
  const popupContentStyle: React.CSSProperties = {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "20px",
    width: "90%",
    maxWidth: "700px",
    maxHeight: "80vh",
    overflowY: "auto",
  };
  const closeButtonStyle: React.CSSProperties = {
    padding: "5px 10px",
    backgroundColor: "#e41e26",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    float: "right",
  };
  const editButtonStyle: React.CSSProperties = {
    padding: "5px 10px",
    backgroundColor: "#e41e26",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
  };
  



  return (
    <div style={containerStyle}>
      <Navbar />
      {dealer ? (
        <>
          {/* Dealer Header Card */}
			<div style={mainCardStyle}>
            <Breadcrumbs
  countryId={dealer.country_id}
  countryName={dealer.country_name}
  dealerName={dealer.company_name}
/>
            <h1 style={headerStyle}>{dealer.company_name}</h1>
          </div>

          {/* Newest Report: Multi-Tabbed Sections */}
          <div style={mainCardStyle}>
            <h2 style={subheaderStyle}>Newest Report</h2>
			{reports.length > 0 && reports[0].created_at && (
			<div style={{ marginBottom: "10px", textAlign: "center" }}>
			<h3>Created: {formatDate(reports[0].created_at)}</h3>
			</div>
			)}

			<div style={{ display: "flex", justifyContent: "center", gap: "12px", marginBottom: "16px" }}>
			  <button style={editButtonStyle} onClick={handleCreateReport}>
				New Report
			  </button>
			  <button style={editButtonStyle} onClick={handleDeleteReport}>
				Delete Report
			  </button>
			</div>


            {/* Card 1: Operational Audit */}
            <div style={mainCardStyle}>
              <div style={tabContainerStyle}>
                {card1Tabs.map((tab) => (
                  <div
                    key={tab.key}
                    style={activeCard1Tab === tab.key ? activeTabStyle : tabStyle}
                    onClick={() => setActiveCard1Tab(tab.key)}
                  >
                    <span style={{ flex: 1, cursor: "pointer" }}>{tab.label}</span>
                  </div>
                ))}
              </div>
              <div style={{ position: "relative", paddingTop: "10px" }}>
                <button
                  style={{ ...editButtonStyle, position: "absolute", top: "0", right: "0" }}
                  onClick={() => navigate(editRoutesCard1[activeCard1Tab])}
                >
                  Edit
                </button>
                {card1Tabs.find((tab) => tab.key === activeCard1Tab)?.content}
              </div>
            </div>

            {/* Card 2: Customer & Installation */}
            <div style={mainCardStyle}>
              <div style={tabContainerStyle}>
                {card2Tabs.map((tab) => (
                  <div
                    key={tab.key}
                    style={activeCard2Tab === tab.key ? activeTabStyle : tabStyle}
                    onClick={() => setActiveCard2Tab(tab.key)}
                  >
                    <span style={{ flex: 1, cursor: "pointer" }}>{tab.label}</span>
                  </div>
                ))}
              </div>
              <div style={{ position: "relative", paddingTop: "10px" }}>
                <button
                  style={{ ...editButtonStyle, position: "absolute", top: "0", right: "0" }}
                  onClick={() => navigate(editRoutesCard2[activeCard2Tab])}
                >
                  Edit
                </button>
                {card2Tabs.find((tab) => tab.key === activeCard2Tab)?.content}
              </div>
            </div>

            {/* Card 3: Support & Feedback */}
            <div style={mainCardStyle}>
              <div style={tabContainerStyle}>
                {card3Tabs.map((tab) => (
                  <div
                    key={tab.key}
                    style={activeCard3Tab === tab.key ? activeTabStyle : tabStyle}
                    onClick={() => setActiveCard3Tab(tab.key)}
                  >
                    <span style={{ flex: 1, cursor: "pointer" }}>{tab.label}</span>
                  </div>
                ))}
              </div>
              <div style={{ position: "relative", paddingTop: "10px" }}>
                <button
                  style={{ ...editButtonStyle, position: "absolute", top: "0", right: "0" }}
                  onClick={() => navigate(editRoutesCard3[activeCard3Tab])}
                >
                  Edit
                </button>
                {card3Tabs.find((tab) => tab.key === activeCard3Tab)?.content}
              </div>
            </div>
          </div>

          {/* Historical Reports List */}
			<div style={mainCardStyle}>
		  <h2 style={subheaderStyle}>Historical Reports</h2>
		  {reports
			.filter(r => r.id !== newestReportId)
			.map(report => (
			  <div
				key={report.id}
				style={listItemStyle}
				onClick={() => setSelectedHistoricalReport(report)}
			  >
				<strong>
				  {formatDate(report.created_at!)}{" "}				  
				</strong>				
			  </div>
			))
		  }
		</div>



          + {/* Historical Report Modal */}
		 {selectedHistoricalReport && (
		   <HistoricalReportModal
			 dealerId={id!}
			 report={selectedHistoricalReport}
			 onClose={() => setSelectedHistoricalReport(null)}
		   />
		 )}
        </>
      ) : (
        <p style={{ textAlign: "center" }}>Loading dealer details...</p>
      )}
    </div>
  );
};

export default DealerDetail;
